import React from "react";
import PropTypes from "prop-types";
import { getRatePlansByProperty, getRoomsByProperty } from "../../api";
import {
  DiscountTypeSelector,
  DaysAmountInput,
  ApplyToSection,
  RoomsRateplansDrawer,
} from "./OfferType/index";
import { useOfferTypeState } from "./hooks/useOfferTypeState";
import { EMPTY_LIST } from "./OfferType/offerTypeConstants";
import { computeDiscountValue } from "./utils/offerTypeUtils";

function OfferType({
  onFieldChange,
  promo,
  setIsNextBtnDisabled,
  calculationType,
  setCalculationType,
  crsId,
  onViewRoomsRateplans,
  isEdit = false,
  isDuplicate = false,
  isMarket = false,
  ratePlans = EMPTY_LIST,
  rooms = EMPTY_LIST,
  ratePlansPropertyId = null,
  isReadOnly = false,
}) {
  const {
    // Context values
    selectedProperty,
    properties,

    // State
    selectedProperties,
    promoData,
    calculationOptions,
    isCalculationMethodLocked,
    showRoomsRateplansModal,
    setShowRoomsRateplansModal,
    drawerSelections,
    setDrawerSelections,
    drawerRatePlans,
    setDrawerRatePlans,
    drawerRooms,
    setDrawerRooms,
    drawerDataPropertyId,
    setDrawerDataPropertyId,
    selectedHotelInDrawer,
    setSelectedHotelInDrawer,
    successMessage,
    setSuccessMessage,
    discountLimitError,
    roomsRateplansValue,
    handleRoomsRateplansValueChange,

    // Refs
    hotelSelectContainerRef,
    savedSelectionsBeforeDrawerRef,
    roomsRateplansByPropertyRef,

    // Computed values
    roomsRateplansOptions,
    propertyOptions,
    onPropertiesSearch,
    onPropertiesReset,
    filteredPropertyOptionsForHotel,

    // Handlers
    handlePriceAdjustmentChange,
    handleChange,
    handleDiscountTypeChange,
    handleCalculationTypeChange,
    handleDiscountChange,
    handleDayChange,
    handleKeyDown,
    handleDrawerClose,
    handlePropertiesBlur,
    handlePropertiesChange,
    buildAllRoomRateplanCombos,

    // Helper methods
    getButtonText,
    getButtonClassName,
  } = useOfferTypeState({
    onFieldChange,
    promo,
    setIsNextBtnDisabled,
    calculationType,
    setCalculationType,
    isEdit,
    isDuplicate,
    isMarket,
    ratePlans,
    rooms,
    ratePlansPropertyId,
    crsId,
  });

  // Stacked (GC-created) promos carry an array of discountVO entries, each with
  // its own calculation method. A single promo uses the shared calculationType
  // prop as the source of truth.
  const isStacked = Array.isArray(promoData.rateRule.ruleVO.discountVO);
  const discountVOList = isStacked
    ? promoData.rateRule.ruleVO.discountVO
    : [promoData.rateRule.ruleVO.discountVO];

  const getDiscountValueForEntry = computeDiscountValue;

  return (
    <div className="offer-type">
      {discountVOList.map((discountVO, index) => (
        <div
          key={`DiscountTypeSelectorInput-${discountVO.order}`}
          className="offer-type-group"
        >
          <DiscountTypeSelector
            priceAdjustment={discountVO.priceAdjustment || "DECREASE"}
            onPriceAdjustmentChange={handlePriceAdjustmentChange}
            discountType={discountVO.mCompDiscountType}
            onDiscountTypeChange={handleChange}
            calculationType={
              isStacked
                ? (discountVO.calculationType ?? calculationType)
                : calculationType
            }
            onCalculationTypeChange={(value) =>
              handleCalculationTypeChange(value, index)
            }
            calculationOptions={calculationOptions}
            isCalculationMethodDisabled={isCalculationMethodLocked}
          />

          <DaysAmountInput
            discountType={discountVO.discountType}
            onDiscountTypeChange={handleDiscountTypeChange}
            mCompDiscountType={discountVO.mCompDiscountType}
            discountValue={getDiscountValueForEntry(discountVO)}
            onDiscountChange={handleDiscountChange}
            percentDiscountMap={discountVO.percentDiscountMap}
            fixedDiscountMap={discountVO.fixedDiscountMap}
            onDayChange={handleDayChange}
            onKeyDown={handleKeyDown}
            errorMessage={discountLimitError}
          />
        </div>
      ))}

      <ApplyToSection
        readOnly={isReadOnly}
        isMarket={isMarket}
        isEdit={isEdit}
        selectedProperty={selectedProperty}
        selectedProperties={selectedProperties}
        onPropertiesChange={handlePropertiesChange}
        onPropertiesBlur={handlePropertiesBlur}
        propertyOptions={propertyOptions}
        onPropertiesSearch={onPropertiesSearch}
        onPropertiesReset={onPropertiesReset}
        roomsRateplansValue={roomsRateplansValue}
        onRoomsRateplansChange={handleRoomsRateplansValueChange}
        roomsRateplansOptions={roomsRateplansOptions}
        onSelectClick={() => setShowRoomsRateplansModal(true)}
        buttonText={getButtonText()}
        buttonClassName={getButtonClassName()}
        isButtonDisabled={selectedProperties.length === 0}
        onFieldChange={onFieldChange}
      />

      <RoomsRateplansDrawer
        readOnly={isReadOnly}
        open={showRoomsRateplansModal}
        onClose={handleDrawerClose}
        selectedProperties={selectedProperties}
        selectedHotelInDrawer={selectedHotelInDrawer}
        setSelectedHotelInDrawer={setSelectedHotelInDrawer}
        filteredPropertyOptions={filteredPropertyOptionsForHotel}
        properties={properties}
        selectedProperty={selectedProperty}
        hotelSelectContainerRef={hotelSelectContainerRef}
        drawerRatePlans={drawerRatePlans}
        setDrawerRatePlans={setDrawerRatePlans}
        drawerRooms={drawerRooms}
        setDrawerRooms={setDrawerRooms}
        drawerDataPropertyId={drawerDataPropertyId}
        setDrawerDataPropertyId={setDrawerDataPropertyId}
        drawerSelections={drawerSelections}
        setDrawerSelections={setDrawerSelections}
        promo={promo}
        promoData={promoData}
        onFieldChange={onFieldChange}
        isEdit={isEdit}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        getRatePlansByProperty={getRatePlansByProperty}
        getRoomsByProperty={getRoomsByProperty}
        buildAllRoomRateplanCombos={buildAllRoomRateplanCombos}
        savedSelectionsBeforeDrawerRef={savedSelectionsBeforeDrawerRef}
        roomsRateplansByPropertyRef={roomsRateplansByPropertyRef}
      />
    </div>
  );
}

OfferType.propTypes = {
  onFieldChange: PropTypes.func.isRequired,
  promo: PropTypes.oneOfType([
    PropTypes.shape({
      rateRule: PropTypes.shape({
        ruleVO: PropTypes.shape({
          discountVO: PropTypes.shape({
            priceAdjustment: PropTypes.string,
            mCompDiscountType: PropTypes.number.isRequired,
            discountType: PropTypes.number.isRequired,
            percentDiscountMap: PropTypes.object.isRequired,
            fixedDiscountMap: PropTypes.object.isRequired,
            percentDiscountAmount: PropTypes.number,
            fixedDiscountAmount: PropTypes.number,
          }).isRequired,
        }).isRequired,
      }),
      allowedRateRoomCodes: PropTypes.array,
    }),
    PropTypes.oneOf([null, undefined]),
  ]),
  setIsNextBtnDisabled: PropTypes.func.isRequired,
  calculationType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setCalculationType: PropTypes.func.isRequired,
  crsId: PropTypes.number,
  onViewRoomsRateplans: PropTypes.func,
  isReadOnly: PropTypes.bool,
  isEdit: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  isMarket: PropTypes.bool,
  ratePlans: PropTypes.array,
  rooms: PropTypes.array,
  ratePlansPropertyId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

export default OfferType;
